import type { ParseError, ParseResult } from "@lib/errors";

/** A recursive location descriptor from a GenBank feature table. */
export type GenbankLocation =
  | { type: "single"; position: number }
  | { type: "range"; start: number; end: number }
  | { type: "complement"; location: GenbankLocation }
  | { type: "join"; locations: GenbankLocation[] };

/** A feature parsed from a GenBank feature table. */
export type GenbankFeature = {
  type: string;
  name: string;
  location: GenbankLocation;
  qualifiers: Record<string, string[]>;
};

/** The LOCUS line fields from a GenBank record. */
export type GenbankLocus = {
  name: string;
  length: number;
  type: string;
  topology: string;
  division: string;
  date: string;
};

/** A literature reference from a GenBank record. */
export type GenbankReference = {
  authors: string;
  title: string;
  journal: string;
  pubmed: string;
};

/** A single record parsed from a GenBank flat file. */
export type GenbankRecord = {
  locus: GenbankLocus;
  definition: string;
  accession: string;
  version: string;
  keywords: string;
  source: string;
  organism: string;
  taxonomy: string;
  references: GenbankReference[];
  comment: string;
  dblinks: Record<string, string>;
  features: GenbankFeature[];
  sequence: string;
};

/** The result of parsing a GenBank flat file. */
export type GenbankParseResult = ParseResult<GenbankRecord[]>;

type ParserState = "header" | "features" | "origin";

const FEATURE_NAME_KEYS = [
  "label",
  "gene",
  "ApEinfo_label",
  "name",
  "organism",
  "locus_tag",
  "note",
];

function resolveFeatureName(qualifiers: Record<string, string[]>): string {
  for (const key of FEATURE_NAME_KEYS) {
    const values = qualifiers[key];
    if (values && values.length > 0 && values[0].length > 0) {
      const value = values[0];
      if (key === "note" && value.length > 100) {
        return value.slice(0, 100);
      }
      return value;
    }
  }
  return "Untitled Feature";
}

function parseLocus(line: string): GenbankLocus {
  // LOCUS format:
  // LOCUS       name          length bp/aa  type  topology division date
  // Columns are loosely positional but we parse by tokens.
  const rest = line.slice(12).trim();
  const tokens = rest.split(/\s+/);

  const name = tokens[0] ?? "";
  const length = Number.parseInt(tokens[1] ?? "0", 10);

  // tokens[2] is "bp" or "aa"
  const lengthUnit = tokens[2] ?? "bp";

  let type = "";
  let topology = "linear";
  let division = "";
  let date = "";

  // Remaining tokens after name, length, unit
  const remaining = tokens.slice(3);

  for (const token of remaining) {
    if (
      /^(DNA|RNA|ds-DNA|ss-DNA|ds-RNA|ss-RNA|mRNA|tRNA|rRNA|cRNA)$/i.test(token)
    ) {
      type = token;
    } else if (/^(linear|circular)$/i.test(token)) {
      topology = token.toLowerCase();
    } else if (/^\d{2}-[A-Z]{3}-\d{4}$/.test(token)) {
      date = token;
    } else if (
      /^[A-Z]{3}$/.test(token) &&
      !["DNA", "RNA"].includes(token.toUpperCase())
    ) {
      division = token;
    }
  }

  // If type is empty but unit is "aa", it's protein
  if (type === "" && lengthUnit === "aa") {
    type = "aa";
  }

  return { name, length, type, topology, division, date };
}

export function parseLocation(locationStr: string): GenbankLocation {
  const str = locationStr.trim();

  // complement(...)
  if (str.startsWith("complement(") && str.endsWith(")")) {
    const inner = str.slice(11, -1);
    return { type: "complement", location: parseLocation(inner) };
  }

  // join(...)
  if (str.startsWith("join(") && str.endsWith(")")) {
    const inner = str.slice(5, -1);
    const locations = splitTopLevelCommas(inner).map(parseLocation);
    return { type: "join", locations };
  }

  // range: start..end
  if (str.includes("..")) {
    const [startStr, endStr] = str.split("..");
    return {
      type: "range",
      start: Number.parseInt(startStr, 10),
      end: Number.parseInt(endStr, 10),
    };
  }

  // single position
  return { type: "single", position: Number.parseInt(str, 10) };
}

function splitTopLevelCommas(str: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = "";

  for (const char of str) {
    if (char === "(") {
      depth++;
      current += char;
    } else if (char === ")") {
      depth--;
      current += char;
    } else if (char === "," && depth === 0) {
      parts.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  if (current.trim().length > 0) {
    parts.push(current.trim());
  }

  return parts;
}

export function parseGenbank(text: string): GenbankParseResult {
  const lines = text.split("\n");
  const records: GenbankRecord[] = [];
  const errors: ParseError[] = [];

  let state: ParserState = "header";
  let locus: GenbankLocus = {
    name: "",
    length: 0,
    type: "",
    topology: "linear",
    division: "",
    date: "",
  };
  let definition = "";
  let accession = "";
  let version = "";
  let keywords = "";
  let source = "";
  let organism = "";
  let taxonomy = "";
  let references: GenbankReference[] = [];
  let currentRef: GenbankReference = {
    authors: "",
    title: "",
    journal: "",
    pubmed: "",
  };
  let inReference = false;
  let comment = "";
  let dblinks: Record<string, string> = {};
  let features: GenbankFeature[] = [];
  let sequence = "";

  // Current header keyword for multi-line continuation
  let currentHeaderKey = "";

  // Feature parsing state
  let currentFeatureType = "";
  let currentLocationStr = "";
  let currentQualifiers: Record<string, string[]> = {};
  let currentQualifierKey = "";
  let currentQualifierValue = "";
  let inQualifier = false;
  let locationComplete = false;

  function finishQualifier(): void {
    if (inQualifier && currentQualifierKey) {
      const value = currentQualifierValue;
      if (!currentQualifiers[currentQualifierKey]) {
        currentQualifiers[currentQualifierKey] = [];
      }
      currentQualifiers[currentQualifierKey].push(value);
    }
    inQualifier = false;
    currentQualifierKey = "";
    currentQualifierValue = "";
  }

  function finishFeature(): void {
    finishQualifier();
    if (currentFeatureType) {
      try {
        const location = parseLocation(currentLocationStr);
        const name = resolveFeatureName(currentQualifiers);
        features.push({
          type: currentFeatureType,
          name,
          location,
          qualifiers: currentQualifiers,
        });
      } catch {
        errors.push({
          message: `Failed to parse location for feature '${currentFeatureType}': ${currentLocationStr}`,
        });
      }
    }
    currentFeatureType = "";
    currentLocationStr = "";
    currentQualifiers = {};
    locationComplete = false;
  }

  function finishReference(): void {
    if (inReference) {
      references.push({ ...currentRef });
      currentRef = { authors: "", title: "", journal: "", pubmed: "" };
      inReference = false;
    }
  }

  function finishRecord(): void {
    if (state === "features") {
      finishFeature();
    }

    finishReference();

    records.push({
      locus,
      definition: definition.trim(),
      accession,
      version,
      keywords: keywords.trim(),
      source: source.trim(),
      organism: organism.trim(),
      taxonomy: taxonomy.trim(),
      references,
      comment: comment.trim(),
      dblinks,
      features,
      sequence,
    });

    // Reset state
    state = "header";
    locus = {
      name: "",
      length: 0,
      type: "",
      topology: "linear",
      division: "",
      date: "",
    };
    definition = "";
    accession = "";
    version = "";
    keywords = "";
    source = "";
    organism = "";
    taxonomy = "";
    references = [];
    currentRef = { authors: "", title: "", journal: "", pubmed: "" };
    inReference = false;
    comment = "";
    dblinks = {};
    features = [];
    sequence = "";
    currentHeaderKey = "";
  }

  for (const line of lines) {
    // End of record
    if (line.startsWith("//")) {
      finishRecord();
      continue;
    }

    if (state === "origin") {
      // Sequence lines: numbers and bases, strip everything except letters
      const seqChars = line.replace(/[^a-zA-Z]/g, "").toUpperCase();
      sequence += seqChars;
      continue;
    }

    if (state === "features") {
      // Check for section transitions
      if (line.startsWith("ORIGIN")) {
        finishFeature();
        state = "origin";
        continue;
      }

      if (line.startsWith("BASE COUNT")) {
        continue;
      }

      // Non-indented line means we left features section
      if (line.length > 0 && !line.startsWith(" ") && !line.startsWith("\t")) {
        finishFeature();
        state = "header";
        // Fall through to header processing below
      } else {
        // Feature content line
        const featureContent = line;

        // Feature type line: type at columns 5-20, location at 21+
        // A feature type line has text starting at column 5 (index 5)
        // and the feature type name is NOT preceded by '/'
        if (
          featureContent.length > 5 &&
          featureContent[5] !== " " &&
          featureContent[5] !== "/" &&
          featureContent.slice(0, 5) === "     "
        ) {
          // Check if this is a new feature type (not a qualifier line)
          const typeAndLocation = featureContent.slice(5).trim();
          const spaceIdx = typeAndLocation.indexOf(" ");

          if (spaceIdx > 0) {
            finishFeature();
            currentFeatureType = typeAndLocation.slice(0, spaceIdx);
            currentLocationStr = typeAndLocation.slice(spaceIdx).trim();
            // Check if location is complete (balanced parentheses)
            locationComplete = isBalancedParens(currentLocationStr);
          } else {
            // Just a feature type with no location on this line (unusual)
            finishFeature();
            currentFeatureType = typeAndLocation;
            currentLocationStr = "";
            locationComplete = false;
          }
        } else if (
          featureContent.length > 21 &&
          featureContent.slice(0, 21).trim() === "" &&
          featureContent[21] === "/"
        ) {
          // Qualifier line starting with /
          if (!locationComplete) {
            locationComplete = true;
          }
          finishQualifier();

          const qualStr = featureContent.slice(21);
          const eqIdx = qualStr.indexOf("=");

          if (eqIdx > 0) {
            currentQualifierKey = qualStr.slice(1, eqIdx);
            let value = qualStr.slice(eqIdx + 1);

            // Remove surrounding quotes if present
            if (value.startsWith('"')) {
              value = value.slice(1);
              if (value.endsWith('"')) {
                value = value.slice(0, -1);
                // Complete quoted value
                inQualifier = true;
                currentQualifierValue = value.replace(/""/g, '"');
                finishQualifier();
              } else {
                // Multi-line quoted value
                inQualifier = true;
                currentQualifierValue = value;
              }
            } else {
              // Unquoted value (number or bare token)
              inQualifier = true;
              currentQualifierValue = value;
              finishQualifier();
            }
          } else {
            // Flag qualifier like /pseudo
            currentQualifierKey = qualStr.slice(1);
            inQualifier = true;
            currentQualifierValue = "";
            finishQualifier();
          }
        } else if (
          featureContent.length > 21 &&
          featureContent.slice(0, 21).trim() === ""
        ) {
          // Continuation line (indented past column 21, no leading /)
          if (inQualifier) {
            // Multi-line qualifier continuation
            let contValue = featureContent.slice(21);
            if (contValue.endsWith('"')) {
              contValue = contValue.slice(0, -1);
              currentQualifierValue += contValue.replace(/""/g, '"');
              finishQualifier();
            } else {
              currentQualifierValue += contValue;
            }
          } else if (!locationComplete) {
            // Location continuation
            currentLocationStr += featureContent.slice(21).trim();
            locationComplete = isBalancedParens(currentLocationStr);
          }
        }
        continue;
      }
    }

    // Header processing
    if (line.startsWith("LOCUS")) {
      locus = parseLocus(line);
      currentHeaderKey = "LOCUS";
    } else if (line.startsWith("DEFINITION")) {
      definition = line.slice(12).trim();
      currentHeaderKey = "DEFINITION";
    } else if (line.startsWith("ACCESSION")) {
      accession = line.slice(12).trim();
      currentHeaderKey = "ACCESSION";
    } else if (line.startsWith("VERSION")) {
      version = line.slice(12).trim();
      currentHeaderKey = "VERSION";
    } else if (line.startsWith("KEYWORDS")) {
      keywords = line.slice(12).trim();
      currentHeaderKey = "KEYWORDS";
    } else if (line.startsWith("SOURCE")) {
      source = line.slice(12).trim();
      currentHeaderKey = "SOURCE";
    } else if (line.startsWith("  ORGANISM")) {
      organism = line.slice(12).trim();
      currentHeaderKey = "ORGANISM";
    } else if (line.startsWith("REFERENCE")) {
      finishReference();
      inReference = true;
      currentHeaderKey = "REFERENCE";
    } else if (line.startsWith("  AUTHORS")) {
      currentRef.authors = line.slice(12).trim();
      currentHeaderKey = "AUTHORS";
    } else if (line.startsWith("  TITLE")) {
      currentRef.title = line.slice(12).trim();
      currentHeaderKey = "TITLE";
    } else if (line.startsWith("  JOURNAL")) {
      currentRef.journal = line.slice(12).trim();
      currentHeaderKey = "JOURNAL";
    } else if (line.startsWith("   PUBMED")) {
      currentRef.pubmed = line.slice(12).trim();
      currentHeaderKey = "PUBMED";
    } else if (line.startsWith("COMMENT")) {
      comment = line.slice(12).trim();
      currentHeaderKey = "COMMENT";
    } else if (line.startsWith("DBLINK")) {
      const dblinkValue = line.slice(12).trim();
      const colonIdx = dblinkValue.indexOf(":");
      if (colonIdx > 0) {
        dblinks[dblinkValue.slice(0, colonIdx).trim()] = dblinkValue
          .slice(colonIdx + 1)
          .trim();
      }
      currentHeaderKey = "DBLINK";
    } else if (line.startsWith("FEATURES")) {
      finishReference();
      state = "features";
      currentHeaderKey = "";
    } else if (line.startsWith("ORIGIN")) {
      finishReference();
      state = "origin";
      currentHeaderKey = "";
    } else if (line.startsWith("BASE COUNT") || line.startsWith("PRIMARY")) {
      currentHeaderKey = "";
    } else if (line.startsWith(" ") || line.startsWith("\t")) {
      const continuation = line.trim();
      switch (currentHeaderKey) {
        case "DEFINITION":
          definition += ` ${continuation}`;
          break;
        case "KEYWORDS":
          keywords += ` ${continuation}`;
          break;
        case "SOURCE":
          source += ` ${continuation}`;
          break;
        case "ORGANISM":
          taxonomy += taxonomy ? ` ${continuation}` : continuation;
          break;
        case "AUTHORS":
          currentRef.authors += ` ${continuation}`;
          break;
        case "TITLE":
          currentRef.title += ` ${continuation}`;
          break;
        case "JOURNAL":
          currentRef.journal += ` ${continuation}`;
          break;
        case "COMMENT":
          comment += `\n${continuation}`;
          break;
        case "DBLINK": {
          const colonIdx = continuation.indexOf(":");
          if (colonIdx > 0) {
            dblinks[continuation.slice(0, colonIdx).trim()] = continuation
              .slice(colonIdx + 1)
              .trim();
          }
          break;
        }
      }
    }
  }

  return { result: records, errors };
}

function isBalancedParens(str: string): boolean {
  let depth = 0;
  for (const char of str) {
    if (char === "(") depth++;
    if (char === ")") depth--;
    if (depth < 0) return false;
  }
  return depth === 0;
}
